/**
 * @module root
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
/* tslint:disable */

import { setSettings, setKeyPair } from '@environment/settings';
import * as CryptoJS from 'crypto-js';
import { Base64URL, utf8ToUint8Array, createGUID } from '@support/shared/services/encoding-utils';
import { from, Observable } from 'rxjs';
import { SHARED_CONSTANTS } from './shared/constants/shared-constants.constants';
import { WorkerUtils } from '@core/worker/models/utils';
import { parse as yamlParse} from 'yaml'
declare const keyWorker: Worker;
/**
 * ConfigLoader class to load config.json
 * and provide values of configs
 */

export class ConfigLoader {
  public static data: object = null;

  /**
   * load method to load config.json
   * @param jsonFile: string
   * @return Promise<boolean>
   */
  public static load(): Promise<boolean> {
    const jsonFile = ConfigLoader.getConfigPath();
    return new Promise((resolve: any, reject: any) => {
      const xobj = new XMLHttpRequest();
      xobj.open('GET', jsonFile, false);
      xobj.overrideMimeType('application/x-yaml');
      xobj.setRequestHeader('cache-control', 'no-cache');
      xobj.setRequestHeader('cache-control', 'max-age=0');
      xobj.setRequestHeader('Pragma', 'no-cache');
      xobj.setRequestHeader('Expires', '0');
      xobj.onreadystatechange = () => {
        const readyOK = 4;
        const responseOK = 200;
        if (xobj.readyState === readyOK) {
          if (xobj.status === responseOK) {
            ConfigLoader.manageServerOffset(xobj.getResponseHeader('Date'));
            ConfigLoader.data = yamlParse(xobj.responseText);
            setSettings(ConfigLoader.data);
            this.callAemEnvConfig(reject);
            this.postConfigLoad(resolve);
          } else {
            reject(`Could not load file '${jsonFile}': ${xobj.status}`);
          }
        }
      };
      xobj.send(null);
    });
  }

  private static postConfigLoad(resolve: any): void {
    ConfigLoader.handlePOPKeyStructure().then((value) => {
      resolve(true);
    });
  }

  private static callAemEnvConfig(reject: any): void {
    const parseConfig = (configKey: string, defaultValue: Object | Array<any>, configuration) => {
      let parsedValue = null;
      const unparsedValue = configuration[configKey];
      try {
        if ((typeof unparsedValue) == 'string') {
          parsedValue = JSON.parse(unparsedValue);
        }
      } catch (e) {
        parsedValue = defaultValue;
      }
      return parsedValue;
    };
    const domain: string = ConfigLoader.data['aemEndPoint'];
    const env: string = ConfigLoader.data['featureToggleConfig'].metaData.environment;
    const xobjAEMConfig = new XMLHttpRequest();
    const aemConfig = `${domain}/content/t-mobile/en/configurations/ui-config/${env}.config.json`;
    xobjAEMConfig.open('GET', aemConfig, false);
    xobjAEMConfig.onreadystatechange = () => {
      const readyOK = 4;
      const responseOK = 200;
      if (xobjAEMConfig.readyState === readyOK && xobjAEMConfig.status === responseOK) {
        const parsedConfig = parseConfig('responseText', {}, xobjAEMConfig);
        parsedConfig.devicesPromos = parseConfig('devicesPromos', {}, parsedConfig);
        parsedConfig.plansPromos = parseConfig('plansPromos', {}, parsedConfig);
        ConfigLoader.data = {...ConfigLoader.data, ...parsedConfig};
        setSettings(ConfigLoader.data);
      } else {
        reject(`Could not load file '${aemConfig}': ${xobjAEMConfig.status}`);
      }
    };
    xobjAEMConfig.send(null);
  }

  /**
   * getConfig method to provide values of given keys
   * @param key: string
   * @return any
   */
  public getConfig(key: string): any {
    if (ConfigLoader.data == null) {
      return null;
    }
    if (key in ConfigLoader.data) {
      return ConfigLoader.data[key];
    }
    return null;
  }

  private static getConfigPath(): string {
      return '/config/config.yml';
  }

  private static generateKeys() {
    return self.crypto.subtle
      .generateKey(
        {
          name: 'RSASSA-PKCS1-v1_5',
          modulusLength: 2048,
          publicExponent: new Uint8Array([0x01, 0x00, 0x01]),
          hash: { name: 'SHA-256' },
        },
        false,
        ['sign', 'verify']
      )
      .then(
        (key) => ({ publicKey: key.publicKey, privateKey: key.privateKey }),
        (err) => {
          console.error(err);
          throw err;
        }
      );
  }

  private static handlePOPKeyStructure() {
    return new Promise((resolve: any, reject: any) => {
      const dbName = 'key-db';
      const objectStoreName = 'key-store';
      const keyName = 'key-pair';
      if (!self.indexedDB && WorkerUtils.isIE()) {
        ConfigLoader.handleIndexDBFallback().then(() => {
          resolve(true);
        });
      } else if (!self.indexedDB) {
        ConfigLoader.generateKeys().then((newKeyPair) => {
          self['keyPair'] = newKeyPair;
          self.crypto.subtle.exportKey('spki', newKeyPair.publicKey).then((key) => {
            const pemKeybase64 = self.btoa(String.fromCharCode.apply(null, new Uint8Array(key)));
            self['pKey'] = `-----BEGIN PUBLIC KEY----- ${pemKeybase64} -----END PUBLIC KEY-----`;
            resolve(true);
          });
        });
      } else {
        const indexedDB = self.indexedDB;
        const open = indexedDB.open(dbName, 1);
        open.onupgradeneeded = function () {
          const db = open.result;
          db.createObjectStore(objectStoreName);
        };

        open.onsuccess = function () {
          const db = open.result;
          let objectStore = db
            .transaction(objectStoreName, 'readonly')
            .objectStore(objectStoreName);

          objectStore.get(keyName).onsuccess = function (event) {
            let keyPair = event.target['result'];
            if (!keyPair) {
              if (!WorkerUtils.isIE()) {
                ConfigLoader.generateKeys().then((newKeyPair) => {
                  objectStore = db
                    .transaction(objectStoreName, 'readwrite')
                    .objectStore(objectStoreName);
                  objectStore.add(newKeyPair, keyName);
                  console.log('Added new keys');
                  self['keyPair'] = newKeyPair;

                  self.crypto.subtle.exportKey('spki', newKeyPair.publicKey).then((key) => {
                    const pemKeybase64 = self.btoa(
                      String.fromCharCode.apply(null, new Uint8Array(key))
                    );
                    self['pKey'] =
                      `-----BEGIN PUBLIC KEY----- ${pemKeybase64} -----END PUBLIC KEY-----`;
                    resolve(true);
                  });

                  db.close();
                });
              } else {
                ConfigLoader.handleIndexDBFallback().then((newKeyPair) => {
                  objectStore = db
                    .transaction(objectStoreName, 'readwrite')
                    .objectStore(objectStoreName);
                  objectStore.add(newKeyPair, keyName);
                  console.log('Added new keys');
                  resolve(true);
                });
              }
            } else {
              self['keyPair'] = keyPair;
              console.log('Using Old keys');
              if (!WorkerUtils.isIE()) {
                self.crypto.subtle.exportKey('spki', keyPair.publicKey).then((key) => {
                  const pemKeybase64 = self.btoa(
                    String.fromCharCode.apply(null, new Uint8Array(key))
                  );
                  self['pKey'] =
                    `-----BEGIN PUBLIC KEY----- ${pemKeybase64} -----END PUBLIC KEY-----`;
                  resolve(true);
                });
              } else {
                const expKeyStruct = self['msCrypto'].subtle.exportKey('spki', keyPair.publicKey);
                expKeyStruct.oncomplete = function (evt) {
                  const pemKeybase64 = self.btoa(
                    String.fromCharCode.apply(null, new Uint8Array(evt.currentTarget.result))
                  );
                  self['pKey'] =
                    `-----BEGIN PUBLIC KEY----- ${pemKeybase64} -----END PUBLIC KEY-----`;
                  resolve(true);
                };
              }
              db.close();
            }
          };
        };

        // Incase indexedDB failed to open
        open.onerror = function () {
          ConfigLoader.generateKeys().then((newKeyPair) => {
            self['keyPair'] = newKeyPair;
            if (!WorkerUtils.isIE()) {
              self.crypto.subtle.exportKey('spki', newKeyPair.publicKey).then((key) => {
                const pemKeybase64 = self.btoa(
                  String.fromCharCode.apply(null, new Uint8Array(key))
                );
                self['pKey'] =
                  `-----BEGIN PUBLIC KEY----- ${pemKeybase64} -----END PUBLIC KEY-----`;
                resolve(true);
              });
            } else {
              const expKeyStruct = self['msCrypto'].subtle.exportKey('spki', newKeyPair.publicKey);
              expKeyStruct.oncomplete = function (evt) {
                const pemKeybase64 = self.btoa(
                  String.fromCharCode.apply(null, new Uint8Array(evt.currentTarget.result))
                );
                self['pKey'] =
                  `-----BEGIN PUBLIC KEY----- ${pemKeybase64} -----END PUBLIC KEY-----`;
                resolve(true);
              };
            }
          });
        };
      }
    });
  }

  /**
   * crypto signature login starts here
   */

  public generatePartialToken(
    endpointConfig: string,
    payload: string,
    method: string,
    kid: string,
    activityId: string
  ): string {
    const header = { alg: 'RS256', typ: 'JWT', kid: kid };
    return (
      Base64URL.stringify(utf8ToUint8Array(JSON.stringify(header), self.btoa)) +
      '.' +
      Base64URL.stringify(
        utf8ToUint8Array(
          this.generateEhtsFormat(endpointConfig, payload, method, activityId),
          self.btoa
        )
      )
    );
  }

  private generateEhtsFormat(
    url: string,
    payload: string,
    method: string,
    activityId: string
  ): string {
    const ehtsKeyValueMap = new Map<string, string>();
    ehtsKeyValueMap.set('Content-Type', 'application/json');
    ehtsKeyValueMap.set('uri', '/' + url);
    ehtsKeyValueMap.set('http-method', method);
    ehtsKeyValueMap.set('body', payload);
    ehtsKeyValueMap.set('channelId', 'WEB');
    ehtsKeyValueMap.set('activityId', activityId);
    let strEhts = '';
    let strToHash = '';
    ehtsKeyValueMap.forEach((value: string, key: string) => {
      if (strEhts.length > 0) {
        strEhts = strEhts + ';';
      }
      strEhts = strEhts + key;
      strToHash = strToHash + value;
    });
    console.log('ehts', strEhts);
    console.log('edts', strToHash);
    // validity logic
    const jti = createGUID();
    const edts = CryptoJS.SHA256(strToHash)
      .toString(CryptoJS.enc.Base64)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/\=+$/, '');

    const serverTimeDiff = Number(sessionStorage.getItem('serverLoadTime'));
    const iat = Number(Date.parse(new Date().toUTCString()) / 1000) + serverTimeDiff;
    const expTime = iat + (ConfigLoader.data['tokenExp'] ? ConfigLoader.data['tokenExp'] : 2 * 60);

    //Base64URL.stringify(utf8ToUint8Array(strToHash, self.btoa));
    return JSON.stringify({
      edts: edts,
      v: '1',
      exp: expTime,
      ehts: strEhts,
      iat: iat,
      jti: jti,
    });
  }

  public generatePopToken(partialToken: string): Observable<ArrayBuffer> {
    const DefaultAlgorithms = {
      SIGN: {
        name: 'RSASSA-PKCS1-v1_5',
        // Must include the `hash` here so Edge does not error out.
        hash: { name: 'SHA-256' },
      },
    };
    const key: CryptoKey = self['keyPair'].privateKey;
    if (!WorkerUtils.isIE()) {
      return from(
        self.crypto.subtle.sign(
          DefaultAlgorithms.SIGN,
          key,
          utf8ToUint8Array(partialToken, self.btoa)
        )
      );
    } else {
      return from(ConfigLoader.signForIE(key, partialToken));
    }
  }

  public static triggerKeyMap(pem: string): ArrayBuffer {
    function str2ab(str: string) {
      const buf = new ArrayBuffer(str.length);
      const bufView = new Uint8Array(buf);
      for (let i = 0, strLen = str.length; i < strLen; i++) {
        bufView[i] = str.charCodeAt(i);
      }
      return buf;
    }
    // base64 decode the string to get the binary data
    const binaryDerString = self.atob(pem);
    // convert from a binary string to an ArrayBuffer
    const binaryDer = str2ab(binaryDerString);
    return binaryDer;
  }

  public static generateKeyMapForRAP() {
    return new Promise((resolve: any, reject: any) => {
      const keyPairString = self['RepdashStore'].stateStore.getState('rep.ppkMap');
      const keyPair = keyPairString.split('.');
      if (!WorkerUtils.isIE()) {
        self.crypto.subtle
          .importKey(
            'pkcs8',
            ConfigLoader.triggerKeyMap(keyPair[1]),
            {
              name: 'RSASSA-PKCS1-v1_5',
              hash: { name: 'SHA-256' },
            },
            true,
            ['sign']
          )
          .then(
            (key) => {
              console.log('configloader key', key);
              const finalKeyMap = {
                privateKey: key,
                publicKey:
                  SHARED_CONSTANTS.PUB_PEM_HEADER_START +
                  keyPair[0] +
                  SHARED_CONSTANTS.PUB_PEM_HEADER_END,
              };
              self['keyPair'] = finalKeyMap;
              setKeyPair(finalKeyMap);
              resolve(true);
            },
            (error) => {
              console.log('error', error);
              resolve(true);
            }
          );
      } else {
        const keyOps = self['msCrypto'].subtle.importKey(
          'pkcs8',
          ConfigLoader.triggerKeyMap(keyPair[1]),
          {
            name: 'RSASSA-PKCS1-v1_5',
            hash: { name: 'SHA-256' },
          },
          true,
          ['sign']
        );
        keyOps.oncomplete = function (evt) {
          const finalKeyMap = {
            privateKey: evt.target.result,
            publicKey:
              SHARED_CONSTANTS.PUB_PEM_HEADER_START +
              keyPair[0] +
              SHARED_CONSTANTS.PUB_PEM_HEADER_END,
          };
          self['keyPair'] = finalKeyMap;
          setKeyPair(finalKeyMap);
          resolve(true);
        };
        keyOps.onerror = function (evt) {
          resolve(true);
        };
      }
    });
  }

  private static manageServerOffset(serverTime) {
    console.log(serverTime);
    sessionStorage.setItem('sessionCounter', '1');

    let systemTimeMillisGMT = Number(Date.parse(new Date().toUTCString()));
    let serverTimeMillisGMT = Number(Date.parse(new Date(Date.parse(serverTime)).toUTCString()));
    let serverTimeDiffMillisGMT = serverTimeMillisGMT - systemTimeMillisGMT;
    sessionStorage.setItem('serverLoadTime', (serverTimeDiffMillisGMT / 1000).toString());

    setInterval(() => {
      let sessionCounter = sessionStorage.getItem('sessionCounter');
      sessionCounter = (Number(sessionCounter) + 1).toString();
      sessionStorage.setItem('sessionCounter', sessionCounter);
    }, 1000);
  }

  private static handleIndexDBFallback() {
    return new Promise((resolve: any, reject: any) => {
      const cryptoSubtle = self['msCrypto'].subtle.generateKey(
        {
          name: 'RSASSA-PKCS1-v1_5',
          modulusLength: 2048,
          publicExponent: new Uint8Array([0x01, 0x00, 0x01]),
          hash: { name: 'SHA-256' },
        },
        false,
        ['sign', 'verify']
      );

      cryptoSubtle.oncomplete = function (key) {
        let newKeyPair = {
          publicKey: key.target.result.publicKey,
          privateKey: key.target.result.privateKey,
        };
        self['keyPair'] = newKeyPair;
        const cryptoExportSubtle = self['msCrypto'].subtle.exportKey('spki', newKeyPair.publicKey);
        cryptoExportSubtle.oncomplete = function (e) {
          let publicKeyValue = e.currentTarget.result;
          const pemKeybase64 = self.btoa(
            String.fromCharCode.apply(null, new Uint8Array(publicKeyValue))
          );
          self['pKey'] = `-----BEGIN PUBLIC KEY----- ${pemKeybase64} -----END PUBLIC KEY-----`;
          resolve(newKeyPair);
        };
      };
    });
  }

  private static signForIE(key: CryptoKey, partialToken: string): Promise<any> {
    return new Promise((resolve: any, reject: any) => {
      //const winCrypto = this.objectService.get(self, 'msCrypto');
      const DefaultAlgorithms = {
        SIGN: {
          name: 'RSASSA-PKCS1-v1_5',
          // Must include the `hash` here so Edge does not error out.
          hash: { name: 'SHA-256' },
        },
      };
      const signObj = self['msCrypto'].subtle.sign(
        DefaultAlgorithms.SIGN,
        key,
        utf8ToUint8Array(partialToken, self.btoa)
      );
      signObj.oncomplete = function (event: any) {
        resolve(event.target.result);
      };
    });
  }
  constructor() {}
}
